import React, {forwardRef, useContext, useEffect, useImperativeHandle, useState} from 'react';
import './style.scss'
import TableContainer from "@material-ui/core/TableContainer";
import Table from "@material-ui/core/Table";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import TableBody from "@material-ui/core/TableBody";
import Paper from "@material-ui/core/Paper";
import CircularProgress from "@material-ui/core/CircularProgress/CircularProgress";
import axios from "axios";
import {AppContext} from "../../../../../../Utils/AppContext";
import Input from "@material-ui/core/Input";
import moment from 'moment';
import UpdateIcon from '@material-ui/icons/UpdateRounded';
import Button from "@material-ui/core/Button";
import {splitToChunks} from "../../../../../../../Utils/ArrayUtils";
import {EnhancedTableHead} from "../../../../../../Utils/TableUtils/EnhancedTableHead";
import {getComparator, stableSort} from "../../../../../../Utils/TableUtils/Functions";

const MessengerOrdersHistory = forwardRef((props, ref) => {
    const appContext = useContext(AppContext);
    const [isLoading, setLoading] = useState(undefined);
    const [orders, setOrders] = useState(undefined);
    const [startTime, setStartTime] = useState(undefined);
    const [startTimeText, setStartTimeText] = useState(undefined);
    const [endTime, setEndTime] = useState(undefined);
    const [endTimeText, setEndTimeText] = useState(undefined);
    const [queryTimeRange, setQueryTimeRange] = useState({});
    const [order, setOrder] = React.useState('desc');
    const [orderBy, setOrderBy] = React.useState('orderTime');
    const [columns, setColumns] = React.useState([]);


    async function queryOrders() {
        setLoading(true);

        const {data: newOrders} = await axios.get('api/v2a/getOrders', {
            params: {
                bid: appContext.bid,
                messengerId: props.messenger.id,
                timestampGt: queryTimeRange.startTime,
                timestampLt: queryTimeRange.endTime,
                includeArchived: true
            }
        });

        let ordersOwesIndex = {};

        if (props.business.isMessengersOrdersOweEnabled) {
            const chunksOrderIds = splitToChunks(newOrders.map(order => order.id), 100);

            const ordersOwes = [];

            for (let chunkedOrderIds of chunksOrderIds) {
                const {data: chunkOrdersOwes} = await axios.get('api/v2/messengers/owes', {
                    params: {
                        bid: appContext.bid,
                        messengerId: props.messenger.id,
                        orderIds: chunkedOrderIds
                    }
                });

                ordersOwes.push(...chunkOrdersOwes)
            }

            ordersOwesIndex = ordersOwes.reduce(function (result, item) {
                result[item.orderId] = item;
                return result;
            }, {});
        }

        for (let currOrder of newOrders) {
            if (!currOrder.deliveredTime) {
                currOrder.lateMinutes = null;
            } else {
                const orderTime = moment(currOrder.orderTime);
                const deliveredTime = moment(currOrder.deliveredTime);

                const minutesTillDelivered = moment.duration(deliveredTime.diff(orderTime)).asMinutes();
                currOrder.lateMinutes = Math.floor(minutesTillDelivered - currOrder.sla);
            }

            if (currOrder.id in ordersOwesIndex) {
                currOrder.remainingOwe = ordersOwesIndex[currOrder.id].remainingOwe;
                currOrder.totalOwe = ordersOwesIndex[currOrder.id].totalOwe;
            } else {
                currOrder.remainingOwe = null;
                currOrder.totalOwe = null;
            }
        }

        setOrders(newOrders);

        setLoading(false);
    }

    useImperativeHandle(ref, () => ({
        refreshOrders() {
            queryOrders();
        }
    }));

    useEffect(() => {
        const newColumns = [];

        if (props.business.placesOfDeparture)
            newColumns.push({id: 'placeOfDeparture', label: 'עסק'});

        newColumns.push(...[
            {id: 'label', label: '#'},
            {id: 'address', label: 'כתובת'},
            {id: 'orderTime', label: 'זמן הזמנה'},
            {id: 'lateMinutes', label: 'זמן מסירה'}
        ]);

        if (props.business.isMessengersOrdersOweEnabled)
            newColumns.push({id: 'remainingOwe', label: 'זיכוי'});

        setColumns(newColumns);
    }, [props.business.isMessengersOrdersOweEnabled]);

    function getOrdersSummaryText() {
        const queryStartTimeFormatted = moment(queryTimeRange.startTime).format('DD/MM/YYYY, HH:mm');
        const queryEndTimeFormatted = moment(queryTimeRange.endTime).format('DD/MM/YYYY, HH:mm');

        let numOfOrders;

        if (orders.length === 0)
            numOfOrders = <span className="summary-orders">השליח לא ביצע הזמנות</span>;
        else if (orders.length === 1)
            numOfOrders = <span className="summary-orders">השליח ביצע הזמנה אחת</span>;
        else
            numOfOrders = <span className="summary-orders">השליח ביצע {orders.length} הזמנות</span>;

        return (
            <div>
                {numOfOrders} בין <span className="summary-start-time">{queryStartTimeFormatted}</span> לבין <span
                className="summary-end-time">{queryEndTimeFormatted}</span>
            </div>
        )
    }

    function onStartTimeChanged(value) {
        const newStartTime = moment(value, 'YYYY-MM-DDTHH:mm');

        setStartTimeText(value);
        setStartTime(newStartTime.isValid() ? newStartTime.format() : undefined);
    }

    function onEndTimeChanged(value) {
        const newEndTime = moment(value, 'YYYY-MM-DDTHH:mm');

        setEndTimeText(value);
        setEndTime(newEndTime.isValid() ? newEndTime.format() : undefined);
    }

    function onUpdateTimeRange() {
        if (startTime && endTime) {
            setQueryTimeRange({startTime, endTime});
        }
    }

    function onRequestSort(event, property) {
        setOrder(orderBy === property && order === 'desc' ? 'asc' : 'desc');
        setOrderBy(property);
    }

    useEffect(() => {
        const newStartTime = moment().startOf('month');
        const newEndTime = moment();

        setStartTime(newStartTime.format());
        setStartTimeText(newStartTime.format('YYYY-MM-DDTHH:mm'));

        setEndTime(newEndTime.format());
        setEndTimeText(newEndTime.format('YYYY-MM-DDTHH:mm'));

        setQueryTimeRange({
            startTime: newStartTime.format(),
            endTime: newEndTime.format()
        });
    }, []);

    useEffect(() => {
        if (props.messenger && queryTimeRange.startTime && queryTimeRange.endTime)
            queryOrders();
        else
            setOrders();
    }, [props.messenger, queryTimeRange]);

    if (isLoading === undefined)
        return null;

    return (
        <div className="messenger-orders-history">
            <div className="orders-history-time-range-inputs">
                טווח זמנים:
                &nbsp;
                <Input
                    id="datetime-local"
                    label="זמן התחלת הזמנות"
                    type="datetime-local"
                    value={startTimeText}
                    onChange={event => onStartTimeChanged(event.target.value)}
                    error={!startTime || (endTime && startTime > endTime)}
                />
                <div className="time-range-arrow">←</div>
                <Input
                    id="datetime-local"
                    label="זמן סיום הזמנות"
                    type="datetime-local"
                    value={endTimeText}
                    onChange={event => onEndTimeChanged(event.target.value)}
                    error={!endTime || (startTime && endTime < startTime)}
                />
                {
                    (queryTimeRange.startTime !== startTime || queryTimeRange.endTime !== endTime) && (startTime && endTime && startTime < endTime) ?
                        <Button className="apply-time-range-button" size="medium" color="primary"
                                onClick={onUpdateTimeRange}
                                variant="contained">
                            <UpdateIcon/>
                            &nbsp;
                            החל תאריכים
                        </Button>
                        :
                        null
                }
            </div>
            {isLoading ?
                <div className="loader-container">
                    <CircularProgress size={80} thickness={2}/>
                </div>
                :
                <React.Fragment>
                    {
                        orders ?
                            <div className="orders-history-summary">
                                {getOrdersSummaryText()}
                            </div>
                            :
                            null
                    }
                </React.Fragment>
            }
            {orders && orders.length > 0 ?
                <TableContainer className="orders-history-table" component={Paper}>
                    <Table stickyHeader aria-label="simple table">
                        <EnhancedTableHead
                            headCells={columns}
                            order={order}
                            orderBy={orderBy}
                            onRequestSort={onRequestSort}
                        />
                        <TableBody>
                            {stableSort(orders,
                                getComparator({
                                    "asc": "desc",
                                    "desc": "asc"
                                }[order], orderBy)).map((order) => (
                                <TableRow key={order.id}>
                                    {
                                        props.business.placesOfDeparture ?
                                            <TableCell>{order.placeOfDeparture}</TableCell>
                                            :
                                            null
                                    }
                                    <TableCell>{order.label}</TableCell>
                                    <TableCell>{order.address}</TableCell>
                                    <TableCell>{moment(order.orderTime).format('DD/MM/YYYY, HH:mm')}</TableCell>
                                    <TableCell className="is-late-cell">
                                        {!order.deliveredTime ?
                                            <span>במהלך שילוח</span>
                                            :
                                            <React.Fragment>
                                                {
                                                    order.lateMinutes > 0 ?
                                                        <span
                                                            className="late-text">איחור ({order.lateMinutes} דק׳)</span>
                                                        :
                                                        <div className="on-time-text">הגיע בזמן</div>
                                                }
                                            </React.Fragment>
                                        }
                                    </TableCell>
                                    {
                                        props.business.isMessengersOrdersOweEnabled ?
                                            <TableCell className="owe-cash-cell">
                                                {order.totalOwe === null ?
                                                    <span>לא חייב</span>
                                                    :
                                                    <React.Fragment>
                                                        {
                                                            order.remainingOwe > 0 ?
                                                                <span
                                                                    className="owe-cash-text">₪{order.remainingOwe}</span>
                                                                :
                                                                <span className="not-owe-text">זוכה</span>
                                                        }
                                                    </React.Fragment>
                                                }
                                            </TableCell>
                                            :
                                            null
                                    }
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                :
                null
            }
        </div>
    );
});

export default MessengerOrdersHistory;